import { type InquireAndSave_userProfile$key } from '#app/__generated__/relay/InquireAndSave_userProfile.graphql.js';
import { CircularIconButton } from '#app/components/Buttons/CircularIconButton.js';
import { Menu } from '#app/components/Menu/Menu.js';
import { useModal } from '#app/components/Modals/V2/GlobalModalSystem/useModal.js';
import { useOnboardingRedirection } from '#app/components/RedirectToOnboardingModal/useOnboardingRedirection.js';
import { SaveUserButton } from '#app/components/SaveUserButton/SaveUserButton.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useCopyProfileUrl } from '#app/hooks/useCopyProfileUrl.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { useLocation } from '#app/hooks/useLocation.js';
import { useNavigation } from '#app/hooks/useNavigation.js';
import { useSaveUserAsContact } from '#app/hooks/useSaveUserAsContact.js';
import { useURLParameters } from '#app/hooks/useURLParameters.js';
import { formatName } from '#app/utilities/formatName.js';
import { removeSearchParameter } from '#app/utilities/removeSearchParameter.js';
import { WorkWithMe } from './WorkWithMe.js';
import { AddIcon, MoreIcon, ShareAltIcon } from '@contra/icons/icons';
import { useMount } from '@contra/react-hooks/useMount';
import { graphql, useFragment } from '@contra/relay';
import { getRoute } from '@contra/routes';
import { useCallback, useEffect, useState } from 'react';
import { css, styled } from 'styled-components';

const Container = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: center;
    width: 100%;
    gap: 8px;

    ${theme.mediaQueries.lg} {
      max-width: 310px;
    }
  `}
`;

const StyledCircularIconButton = styled(CircularIconButton)`
  ${() => css`
    flex-shrink: 0;
  `}
`;

const analyticsSection = 'profile_sidebar';

export const InquireAndSave = ({
  disableEditing = false,
  showAddContactButton,
  userProfileRef,
}: {
  readonly disableEditing?: boolean;
  readonly showAddContactButton: boolean;
  readonly userProfileRef: InquireAndSave_userProfile$key;
}) => {
  const location = useLocation();
  const navigation = useNavigation();
  const currentUser = useCurrentUser();

  const profile = useFragment<InquireAndSave_userProfile$key>(
    graphql`
      fragment InquireAndSave_userProfile on UserProfile {
        id
        visitorCanEdit
        displayUsername
        analyticsId
        canReceiveInquiries
        contact {
          id
        }
        avatarImage {
          uid
          id
          url
        }
        firstName
        lastName
        isSavedContact
        hasCompletedIndyOnboarding
        ...WorkWithMe_userProfile
        ...DiscoverUnauthenticatedInquiryModal_profile
      }
    `,
    userProfileRef,
  );

  const isSavedContact = Boolean(profile.isSavedContact);

  const fullName = formatName(
    { firstName: profile.firstName, lastName: profile.lastName },
    'fullName',
  );

  const isViewingOwnProfile = profile.visitorCanEdit;
  const [urlSearchParameters] = useURLParameters('/@username');
  const startInquiryURLParameter = urlSearchParameters.startInquiry;

  const clearStartInquiryURLParameter = useCallback(() => {
    navigation.replace(removeSearchParameter(location.path, 'startInquiry'));
  }, [location.path, navigation]);

  const {
    handleOnboardingRedirectionModalOpen:
      handleClientOnboardingRedirectionModalOpen,
    shouldRedirectUser: shouldRedirectUserToOnboardClient,
  } = useOnboardingRedirection({
    onClientModalClose: clearStartInquiryURLParameter,
    overrideUserType: 'client',
    redirectTo: getRoute('/@username', {
      args: {
        username: profile.displayUsername,
      },
      search: {
        startInquiry: true,
      },
    }),
  });

  const { openModal: openUnauthenticatedInquiryModal } = useModal({
    identifier: 'DiscoverUnauthenticatedInquiryModal',
    modalProps: {
      userProfileRef: profile,
    },
  });

  const canOpenModal =
    !isViewingOwnProfile &&
    !shouldRedirectUserToOnboardClient &&
    profile.canReceiveInquiries;

  const { openModal: openRequestToWorkModal } = useModal({
    identifier: 'RequestToWorkModal',
    modalProps: {
      contractorUserProfileId: profile.id,
    },
  });

  const openInquiryOrOnboardingRedirectionModal = useCallback(() => {
    if (canOpenModal) {
      openRequestToWorkModal();

      if (startInquiryURLParameter) {
        clearStartInquiryURLParameter();
      }
    } else if (
      !currentUser ||
      (!currentUser.userGroups.includes('CLIENT_ONBOARDING_COMPLETE') &&
        currentUser.selectedUserType === 'CLIENT')
    ) {
      openUnauthenticatedInquiryModal();
    } else if (shouldRedirectUserToOnboardClient) {
      handleClientOnboardingRedirectionModalOpen();
    }

    clearStartInquiryURLParameter();
  }, [
    canOpenModal,
    clearStartInquiryURLParameter,
    currentUser,
    handleClientOnboardingRedirectionModalOpen,
    openRequestToWorkModal,
    openUnauthenticatedInquiryModal,
    shouldRedirectUserToOnboardClient,
    startInquiryURLParameter,
  ]);

  // watching for if startInquiry URL param becomes true
  useEffect(() => {
    if (startInquiryURLParameter) {
      if (!isViewingOwnProfile && profile.canReceiveInquiries) {
        openInquiryOrOnboardingRedirectionModal();
      } else {
        clearStartInquiryURLParameter();
      }
    }
    // we only want this effect to run if the url param changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startInquiryURLParameter]);

  const {
    handleOnboardingRedirectionModalOpen,
    shouldRedirectUser: shouldRedirectToOnboarding,
  } = useOnboardingRedirection({
    redirectTo: getRoute('/@username', {
      args: { username: profile.displayUsername },
      search: {
        addToList: true,
      },
    }),
  });

  const { removeUserAsContact, saveUserAsContact } = useSaveUserAsContact({
    analyticsSection,
    contactId: profile?.contact?.id ?? null,
    source: 'ADD_TO_SAVED_PROFILES',
    userProfile: {
      avatarImage: profile?.avatarImage ?? null,
      firstName: profile.firstName ?? '',
      fullName: fullName ?? '',
      id: profile.id,
      isSavedContact,
    },
  });

  const { track } = useAnalytics();

  const handleAddContact = useCallback(() => {
    if (shouldRedirectToOnboarding) {
      handleOnboardingRedirectionModalOpen();
      return;
    }

    track('button', {
      action: 'tapped',
      button_name: 'add_contact',
      owner_id: profile.analyticsId,
      section: analyticsSection,
      switch_value: true,
    });

    saveUserAsContact();
  }, [
    handleOnboardingRedirectionModalOpen,
    saveUserAsContact,
    shouldRedirectToOnboarding,
    track,
    profile.analyticsId,
  ]);

  const handleRemoveContact = useCallback(() => {
    track('button', {
      action: 'tapped',
      button_name: 'remove_contact',
      owner_id: profile.analyticsId,
      section: analyticsSection,
      switch_value: false,
    });

    removeUserAsContact();
  }, [removeUserAsContact, track, profile.analyticsId]);

  const addToList = urlSearchParameters.addToList;

  useMount(() => {
    if (addToList && showAddContactButton) {
      track('button', {
        action: 'tapped',
        button_name: 'add_contact',
        owner_id: profile.analyticsId,
        section: analyticsSection,
        switch_value: true,
      });

      saveUserAsContact();
    }
  });

  const [isActionsOpen, setIsActionsOpen] = useState(false);

  const toggleMenu = () => setIsActionsOpen((previous) => !previous);

  const { onCopyProfileLink } = useCopyProfileUrl({
    successToast: `Copied profile URL`,
    userProfile: {
      displayUsername: profile.displayUsername,
    },
  });

  const handleOpenMoreActionsMenu = useCallback(() => {
    setIsActionsOpen(true);

    track('button', {
      action: 'tapped',
      button_name: 'open_profile_actions_menu',
      section: analyticsSection,
    });
  }, [setIsActionsOpen, track]);

  const handleShareProfile = useCallback(() => {
    onCopyProfileLink();

    track('button', {
      action: 'tapped',
      button_name: 'share_profile',
      section: analyticsSection,
    });
  }, [onCopyProfileLink, track]);

  const handleStartProject = useCallback(() => {
    track('button', {
      action: 'tapped',
      button_name: 'send_profile_proposal',
      section: analyticsSection,
    });

    window.open(
      getRoute('/proposal/new', {
        search: {
          collaborator: profile.displayUsername,
        },
      }),
      '_blank',
    );
  }, [profile.displayUsername, track]);

  const inquiryButton = (
    <WorkWithMe
      disableEditing={disableEditing}
      openInquiryOrOnboardingRedirectionModal={
        openInquiryOrOnboardingRedirectionModal
      }
      profileRef={profile}
    />
  );

  if (!showAddContactButton && !inquiryButton) {
    return null;
  }

  return (
    <Container>
      {inquiryButton ? inquiryButton : null}
      {showAddContactButton ? (
        <>
          <SaveUserButton
            addContact={handleAddContact}
            analyticsSection="profile_sidebar"
            contactId={profile.contact?.id ?? null}
            isSavedContact={isSavedContact}
            removeContact={handleRemoveContact}
            size="large"
            source="ADD_TO_SAVED_PROFILES"
            userName={fullName}
            userProfile={{
              avatar: profile.avatarImage,
              firstName: profile.firstName ?? '',
              id: profile.id,
              isSavedContact,
            }}
          />
          <Menu
            isOpen={isActionsOpen}
            offset={[0, 0]}
            placement="bottom"
            toggleMenu={toggleMenu}
          >
            <Menu.Reference>
              <StyledCircularIconButton
                aria-label="More actions"
                icon={MoreIcon}
                onClick={handleOpenMoreActionsMenu}
                variant="V2TertiarySolid"
              />
            </Menu.Reference>
            <Menu.Panel>
              <Menu.Item
                aria-label="Start project"
                icon={AddIcon}
                onClick={handleStartProject}
              >
                Start project
              </Menu.Item>

              <Menu.Item
                aria-label="Share profile"
                icon={ShareAltIcon}
                onClick={handleShareProfile}
              >
                Share profile
              </Menu.Item>
            </Menu.Panel>
          </Menu>
        </>
      ) : null}
    </Container>
  );
};
